/**
 * Minimal safe markdown for ARIA chat bubbles.
 * Supports **bold**, lists, ### headings, --- hr, and [label](/path) link chips.
 * No HTML passthrough.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { isShareHref } from '../../features/soren/sorenLeavePanel.js'

function isInternalHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//')
}

function inlineNodes(
  text: string,
  keyPrefix: string,
  chipClassName: string,
  onInternalNav?: (href: string) => void,
): ReactNode[] {
  const nodes: ReactNode[] = []
  const re = /(\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\))/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    if (m[2] != null) {
      nodes.push(<strong key={`${keyPrefix}-b${i++}`}>{m[2]}</strong>)
    } else if (m[3] != null && m[4] != null) {
      const label = m[3]
      const href = m[4]
      if (isShareHref(href)) {
        nodes.push(
          <a
            key={`${keyPrefix}-a${i++}`}
            href={href}
            className={chipClassName}
            data-testid="aria-chat-link-chip"
          >
            {label}
          </a>,
        )
      } else if (isInternalHref(href)) {
        nodes.push(
          <Link
            key={`${keyPrefix}-a${i++}`}
            to={href}
            className={chipClassName}
            data-testid="aria-chat-link-chip"
            onClick={(e) => {
              if (!onInternalNav) return
              e.preventDefault()
              onInternalNav(href)
            }}
          >
            {label}
          </Link>,
        )
      } else {
        nodes.push(label)
      }
    }
    last = m.index + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes.length ? nodes : [text]
}

function isListLine(line: string): boolean {
  return /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)
}

function listBody(line: string): string {
  return line.replace(/^\s*(?:[-*]|\d+\.)\s+/, '')
}

function headingMatch(line: string): { level: number; text: string } | null {
  const m = /^(#{1,3})\s+(.+)$/.exec(line)
  if (!m) return null
  return { level: m[1].length, text: m[2].trim() }
}

function isHr(line: string): boolean {
  return /^\s*(-{3,}|\*{3}|_{3,})\s*$/.test(line)
}

export default function AriaChatMarkdown({
  text,
  chipClassName = 'aria-chat-link-chip',
  onInternalNav,
}: {
  text: string
  chipClassName?: string
  onInternalNav?: (href: string) => void
}) {
  const lines = text.split('\n')
  const blocks: ReactNode[] = []
  let i = 0
  let bi = 0
  while (i < lines.length) {
    if (isHr(lines[i])) {
      blocks.push(<hr key={`hr-${bi++}`} className="aria-chat-md-hr" data-testid="aria-chat-md-hr" />)
      i += 1
      continue
    }
    const heading = headingMatch(lines[i])
    if (heading) {
      const Tag = heading.level === 1 ? 'h3' : heading.level === 2 ? 'h4' : 'h5'
      blocks.push(
        <Tag key={`h-${bi++}`} className={`aria-chat-md-h aria-chat-md-h${heading.level}`}>
          {inlineNodes(heading.text, `h${bi}`, chipClassName, onInternalNav)}
        </Tag>,
      )
      i += 1
      continue
    }
    if (isListLine(lines[i])) {
      const items: ReactNode[] = []
      const ordered = /^\s*\d+\./.test(lines[i])
      while (i < lines.length && isListLine(lines[i])) {
        items.push(<li key={`li-${bi}-${i}`}>{inlineNodes(listBody(lines[i]), `l${bi}${i}`, chipClassName, onInternalNav)}</li>)
        i += 1
      }
      const ListTag = ordered ? 'ol' : 'ul'
      blocks.push(<ListTag key={`list-${bi++}`} className="aria-chat-md-list">{items}</ListTag>)
      continue
    }
    if (lines[i] === '') {
      blocks.push(<br key={`br-${bi++}`} />)
      i += 1
      continue
    }
    blocks.push(
      <p key={`p-${bi++}`} className="aria-chat-md-p">
        {inlineNodes(lines[i], `p${bi}`, chipClassName, onInternalNav)}
      </p>,
    )
    i += 1
  }
  return <div className="aria-chat-md" data-testid="aria-chat-md">{blocks}</div>
}
